import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Routes } from 'common/enums';
import { getCategoryById } from 'common/assets';
import { CommonLayout } from '../index';
import { Category, Item, AppActions, AppTranslationHelper } from 'common/types';
import { PlusTwoTone, TrashCanOutlineTwoTone, ChevronUpTwoTone, ChevronDownTwoTone } from 'common/icons';
import { getTextIdentifier, setDocumentTitle, exportToJsonFile } from 'common/utils';
import { Search } from 'terminal/components/itemList/components';
import styles from './offerList.module.css';

type OfferListProps = {
  actions: AppActions;
  categories: Category[];
  translation: AppTranslationHelper;
  items: Item[];
};

const OfferList: React.FC<OfferListProps> = ({ categories, items, actions, translation }) => {
  const { formatFinancial, t } = translation;
  const [filteredItems, setFilteredItems] = useState([] as Item[]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const title = [t('admin.title'), t('admin.offers.title', 'Ofertas')];
    setDocumentTitle(title);
  }, [t]);

  // Actualiza la lista filtrada cuando cambian los items
  useEffect(() => {
    const offerItems = filterOfferItems();
    setFilteredItems(offerItems);
  }, [items, searchTerm]);

  const handleOnClickOnDeleteBtn = async (event: React.MouseEvent, itemId: string) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Encontrar el item en la lista
    const itemToUpdate = items.find(item => item.id === itemId);
    
    if (itemToUpdate) {
      // Actualizar el item para quitar la marca de oferta (solo cambiar offer a false)
      const updatedItem = {
        ...itemToUpdate,
        offer: false
      };
      
      // Actualizar en la base de datos
      await actions.item.update(updatedItem);
    }
  };

  const handleMoveUp = async (event: React.MouseEvent, currentItem: Item) => {
    event.preventDefault();
    event.stopPropagation();

    const currentOfferItems = filterOfferItems();
    const currentIndex = currentOfferItems.findIndex((item: Item) => item.id === currentItem.id);

    // Si ya está en la primera posición o no se encuentra, no hacer nada
    if (currentIndex <= 0) return;

    // Obtener el ítem que está antes del actual (con menor sortOrderOffer)
    const previousItem = currentOfferItems[currentIndex - 1];
    // console.log({
    //   previousItem,
    //   currentOfferItems
    // })

    // Intercambiar sortOrderOffer usando la acción swapSortOrder
    actions.item.swapSortOrder(currentItem, previousItem, 'sortOrderOffer');
  };

  const handleMoveDown = async (event: React.MouseEvent, currentItem: Item) => {
    event.preventDefault();
    event.stopPropagation();

    const currentOfferItems = filterOfferItems();
    const currentIndex = currentOfferItems.findIndex((item: Item) => item.id === currentItem.id);

    // Si ya está en la última posición o no se encuentra, no hacer nada
    if (currentIndex === -1 || currentIndex >= currentOfferItems.length - 1) return;

    // Obtener el ítem que está después del actual (con mayor sortOrderOffer)
    const nextItem = currentOfferItems[currentIndex + 1];
    // console.log({
    //   current:currentOfferItems[currentIndex],
    //   nextItem
    // })

    // Intercambiar sortOrderOffer usando la acción swapSortOrder
    actions.item.swapSortOrder(currentItem, nextItem, 'sortOrderOffer');
  };

  const renderPresenter = (data: Item) => {
    const { picture, color, name } = data;
    const style = picture ? { backgroundImage: `url(${picture})` } : { backgroundColor: color || 'inherit' };
    return (
      <div className={styles.presenter} style={style}>
        {!data.picture && getTextIdentifier(name)}
      </div>
    );
  };

  // Filtrar por término de búsqueda
  const filterOfferItems = () => {
    // Siempre filtrar los items eliminados primero
    const activeOfferItems = items
      .filter((item) => !item.isDeleted && item.offer)
      .sort((a, b) => b.sortOrderOffer - a.sortOrderOffer); // Ordenar de forma descendente por sortOrderOffer
    
    // Luego aplicar el filtro de búsqueda si es necesario
    if (!searchTerm || searchTerm.length <= 1) {
      return activeOfferItems;
    }

    return activeOfferItems.filter((item) => {
      return item.name.toUpperCase().includes(searchTerm.toUpperCase());
    });
  };

  // Renderizar un item individual
  const renderItem = (data: Item) => {
    const categoryName = getCategoryById(categories, data.parentId).name;

    return (
      <Link
        key={data.id}
        className={styles.entity}
        to={Routes.AdminItemEdit.replace(':id', `${data.id}`)}
      >
        {renderPresenter(data)}
        <div className={`${styles.td} ${styles.tdName}`}>
          {data.name}
        </div>
        <div className={`${styles.td} ${styles.tdCategory}`}>
          {categoryName.replace('Home Screen', t('common.homeScreen'))}
        </div>
        <div className={`${styles.td}`}>{formatFinancial(data.costPrice)}</div>
        <div className={`${styles.td}`}>{formatFinancial(data.price)}</div>
        <div className={`${styles.td} ${styles.publicStatus}`} title={data.isPublic ? t('admin.items.isPublic') : t('admin.items.isNotPublic')}>
          <div className={styles.orderIconsContainer}>
            <div onClick={(evt) => handleMoveUp(evt, data)} title={t('admin.offers.moveUp')}>
              <ChevronUpTwoTone className={styles.orderIcon} />
            </div>
            <div onClick={(evt) => handleMoveDown(evt, data)} title={t('admin.offers.moveDown')}>
              <ChevronDownTwoTone className={styles.orderIcon} />
            </div>
          </div>
          <svg
            className={`${styles.visibilityIcon} ${data.isPublic ? styles.visibilityIconActive : styles.visibilityIconInactive}`}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {data.isPublic ? (
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
            ) : (
              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
            )}
          </svg>
        </div>
        <button className="btn btn-link" onClick={(evt) => handleOnClickOnDeleteBtn(evt, data.id)}>
          <TrashCanOutlineTwoTone />
        </button>
      </Link>
    );
  };

  // Manejar cambio en el término de búsqueda
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  const renderHead = (
    <Fragment>
      <div className={styles.title}>{t('admin.offers.title', 'Ofertas')}</div>
      <div className="d-flex align-items-center">
        <div className={styles.searchContainer}>
          <Search
            onChange={handleSearchChange}
            placeholder="admin.offers.searchPlaceholder"
          />
        </div>
      </div>
      {/* Botones ocultos
      <Link className="btn btn-primary ml-2" to={Routes.AdminItemCreate}>
        <PlusTwoTone />
        <span>{t('admin.items.addItemLabel')}</span>
      </Link>
      <button className="btn btn-outline-primary ml-2 mr-4" onClick={() => exportToJsonFile(filterOfferItems())}>
        {t('common.exportToJSON')}
      </button>
      */}
    </Fragment>
  );

  const renderBody = (
    <Fragment>
      {filterOfferItems().length ? (
        filteredItems.map((item) => renderItem(item))
      ) : (
        <div className={styles.empty}>{t('admin.offers.emptyListMessage', 'No hay productos en oferta')}</div>
      )}
      {filterOfferItems().length > 0 && filteredItems.length === 0 && (
        <div className={styles.empty}>{t('admin.offers.noFilteredResults', 'No se encontraron resultados con el filtro aplicado')}</div>
      )}
    </Fragment>
  );

  return <CommonLayout head={renderHead} body={renderBody} />;
};

export default OfferList;
